import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from '../../../ui/gridSystem';
import {
  PicWrapper,
  InfoBlockContainer,
  InfoBlock,
  UserInfo,
  FiltersWrapper,
  SortingWrapper,
  Legend,
  CourseCard,
  Column,
  CompletionWrapper,
  CardsWrapper,
} from './style';
import {
  CircleImage,
  Text,
  CTA,
  CustomInput,
  CustomSelect,
  NoResult,
  ProgressBar,
  Inline,
} from '../../../ui/atoms';
import Icon from '../../../ui/atoms/Icon';
import { MODAL_OPEN } from '../../redux/actions';
import {
  COURSE_TYPOLOGY_OPTIONS,
  VISIBILITY_OPTIONS,
  getFilterLabel,
  getFilterOptions,
  getLabelByRole,
} from '../../../utils/labels';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import Pagination from '../../../ui/atoms/Pagination';
import theme from '../../../ui/theme';
import routes from '../../../routes';
import Breadcrumb from '../../../ui/atoms/Breadcrumb';
import {
  COURSE_USERS_ACTIVITIES_GET,
  COURSE_USERS_ACTIVITIES_RESET,
  EVENT_USERS_ACTIVITIES_GET,
  USER_COURSES_GET,
  USER_STATS_GET,
} from '../../redux/actions/statistics';
import { timestampToDate } from '../../../utils/common';
import SortBy from '../../../ui/atoms/SortBy';
import { COURSE_CATEGORIES_GET } from '../../redux/actions/courselist';
import ModalUserGroups, {
  ModalUserGroupsId,
} from '../../../ui/atoms/Modals/ModalUserGroups';
import CustomTooltip from '../../../ui/atoms/CustomTooltip';
import ActivitiesStats from '../../../ui/components/ActivitiesStats';
import TimeIntervalFilter from '../../../ui/atoms/TimeIntervalFilter';
import ModalSupervisorUserLog, {
  ModalSupervisorUserLogId,
} from '../../../ui/atoms/Modals/Supervisor/ModalSupervisorUserLog';

const TutorStatistics = ({
  mediaIsPhone,
  mediaIsTablet,
  user,
  courses,
  activities,
  filters,
  pager,
  groupsPager,
  groupsList,
  categories,
  getUserStats,
  getUserCourses,
  getCategories,
  getCourseUsersActivities,
  getEventUsersActivities,
  modalOpen,
  courseUserActivitiesReset,
}) => {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [activeCardId, setActiveCardId] = useState();
  // filters
  const [selectedFilters, setSelectedFilters] = useState({
    user_id,
    approval_status: 'APPROVED',
  });
  const [searchValue, setSearchValue] = useState();

  useEffect(() => {
    if (mediaIsPhone || mediaIsTablet) navigate(routes.statistics.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaIsPhone, mediaIsTablet]);

  useEffect(() => {
    if (!user_id) navigate(routes.statistics.path);
    else {
      window.scrollTo(0, 0);
      getUserStats(selectedFilters);
      if (categories?.length <= 0) getCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  useEffect(() => {
    if (Object.keys(selectedFilters)?.length > 0) {
      getUserCourses(selectedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const onClickSearch = () => {
    setSelectedFilters({ ...selectedFilters, search_text: searchValue });
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    if (key === 'categories') newFilters.parent_categories = value?.value;
    else newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => getUserCourses({ ...selectedFilters, page });
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  const onClickCard = ({ id: course_id, typology }) => {
    courseUserActivitiesReset();
    if (course_id === activeCardId) {
      setActiveCardId(null);
    } else if (typology === 'event') {
      setActiveCardId(course_id);
      getEventUsersActivities(course_id, user_id);
    } else if (typology === 'course') {
      setActiveCardId(course_id);
      getCourseUsersActivities(course_id, user_id);
    }
  };

  return (
    <div id="userPage">
      <Breadcrumb
        crumbs={[
          {
            label: 'Statistiche',
            onClick: () => navigate(routes.statistics.path),
          },
          {
            label: `Utenti: ${user?.first_name || ''} ${user?.last_name || ''}`,
          },
        ]}
      />
      <Container className="pt-0">
        <section>
          <UserInfo>
            <InfoBlockContainer>
              <PicWrapper>
                <CircleImage
                  src={user?.profile_pic}
                  size={mediaIsPhone ? 118 : 140}
                  name={user}
                />
              </PicWrapper>
              <InfoBlock>
                <Text type="h1.5" color="#1F1F1F">
                  {user?.first_name} {user?.last_name}
                </Text>
                <Column rowGap={8}>
                  <Inline columnGap="16px">
                    <Text type="h3" color="#515151">
                      {user?.ffoo?.value}
                    </Text>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="2"
                      height="14"
                      viewBox="0 0 2 14"
                      fill="none"
                    >
                      <path d="M1 0L1 14" stroke="black" />
                    </svg>
                    <Text type="b4">
                      Matricola: <b>{user?.matricola}</b>
                    </Text>
                  </Inline>
                  <Text type="b4">
                    Divisione:{' '}
                    <b>{user?.work_division?.value ?? 'Non assegnato'}</b>
                  </Text>
                  <Text type="b4">
                    Ruolo: <b>{user?.work_role?.value ?? 'Non assegnato'}</b>
                  </Text>
                  <Text type="b4">
                    Sede: <b>{user?.venue}</b>
                  </Text>
                  <Inline columnGap="4px" alignItems="baseline">
                    <Icon
                      type="mail-02"
                      stroke={theme().colors.textPrimary}
                      size={14.5}
                      strokeWidth={2}
                      clickable={false}
                    />
                    <Text type="b4">
                      Email: <b>{user?.email}</b>
                    </Text>
                  </Inline>
                </Column>
              </InfoBlock>
              <InfoBlock>
                <Column rowGap={8}>
                  <Text type="h2" color="#1F1F1F">
                    Attività su Sisfor
                  </Text>
                  <Text type="b4">
                    Ruolo sulla piattaforma:{' '}
                    <b>
                      {' '}
                      {(user?.lms_roles || []).map((role, i) => (
                        <span key={`role-${i}`}>
                          {i > 0 ? ', ' : ''}
                          {getLabelByRole(role)}
                        </span>
                      ))}
                    </b>
                  </Text>
                  <CTA
                    type="tertiary"
                    btnText="Dashboard tutor"
                    icon="chevron-right"
                    onClick={() =>
                      navigate(`${routes.tutorDashboard.path}/${user_id}`)
                    }
                  />
                </Column>
                <Column rowGap={8}>
                  <Text type="b4">
                    Data iscrizione:{' '}
                    <b>{timestampToDate(user?.created_date)}</b>
                  </Text>
                  <Text type="b4">
                    Primo accesso:{' '}
                    <b>
                      {timestampToDate(user?.first_login, 'DD/MM/YYYY - HH:mm')}
                    </b>
                  </Text>
                  <Text type="b4">
                    Ultimo accesso:{' '}
                    <b>
                      {timestampToDate(user?.last_login, 'DD/MM/YYYY - HH:mm')}
                    </b>
                  </Text>
                  <CTA
                    type="tertiary"
                    btnText="Dettaglio log"
                    icon="chevron-right"
                    onClick={() => modalOpen(ModalSupervisorUserLogId)}
                  />
                </Column>
              </InfoBlock>
              <InfoBlock>
                <Text type="h2" color="#1F1F1F">
                  Gruppi di appartenenza
                </Text>
                <Column rowGap={8}>
                  <Text type="h3.5">
                    {user?.first_name} è presente in {groupsList.length ?? 0}{' '}
                    gruppi globali, e in{' '}
                    {groupsList.filter((group) => group?.owned).length ?? 0} dei
                    gruppi che ha creato
                  </Text>
                  <CTA
                    type="tertiary"
                    btnText="Tutti i gruppi"
                    icon="chevron-right"
                    onClick={() => modalOpen(ModalUserGroupsId)}
                  />
                </Column>
              </InfoBlock>
            </InfoBlockContainer>
          </UserInfo>
        </section>
        <section>
          <Text type="h2" mb={32}>
            Percorsi formativi assegnati all’utente
          </Text>
          <FiltersWrapper>
            <CustomInput
              size="l"
              placeholder="Cosa vuoi cercare?..."
              isSearch
              value=""
              icon="search-sm"
              isClearable
              onChange={(v) => setSearchValue(v)}
              onClickIcon={onClickSearch}
              onKeyPress={(event) => {
                if (event.key === 'Enter') onClickSearch();
              }}
            />
            <CustomSelect
              size="s"
              wrapperId="filter-typology"
              placeholder="Tipologia"
              options={COURSE_TYPOLOGY_OPTIONS}
              onChange={(value) => handleOnChange({ key: 'typology', value })}
              isClearable
            />
            {(Object.keys(filters) || []).map((key, i) => (
              <CustomSelect
                key={`filter-${i}`}
                wrapperId={`filter-${key}`}
                size="s"
                placeholder={getFilterLabel(key)}
                options={getFilterOptions(filters, key)}
                onChange={(value) => handleOnChange({ key, value })}
                isClearable
              />
            ))}
            <CustomSelect
              size="s"
              wrapperId="filter-published"
              placeholder="Stato"
              options={VISIBILITY_OPTIONS}
              onChange={(value) => handleOnChange({ key: 'published', value })}
              isClearable
            />
            <TimeIntervalFilter
              callback={(start_date, end_date) => {
                setSelectedFilters({
                  ...selectedFilters,
                  start_date,
                  end_date,
                });
              }}
              singleMonth
              maxMonthsDiff={1}
            />
          </FiltersWrapper>
          <SortingWrapper>
            <Text type="b3">{pager?.total_items} Percorsi formativi</Text>
            <SortBy
              onChange={(v) => {
                setSelectedFilters({ ...selectedFilters, sort_by: v?.value });
              }}
            />
          </SortingWrapper>
          {courses?.length > 0 ? (
            <>
              {!mediaIsPhone && (
                <Legend>
                  <Text type="statistics-heading">Codice</Text>
                  <Text type="statistics-heading">
                    Titolo percorso formativo
                  </Text>
                  <Text type="statistics-heading">Area Tematica</Text>
                  <CompletionWrapper>
                    <Text type="statistics-heading">Completamento</Text>
                    <CustomTooltip
                      type="primary"
                      title="Completamento"
                      tooltipKey="course_completion"
                    />
                  </CompletionWrapper>
                  <Text type="statistics-heading">Data inizio percorso</Text>
                  <Text type="statistics-heading">Data fine percorso</Text>
                  <Text type="statistics-heading">Stato</Text>
                  <Text type="statistics-heading">Data di pubblicazione</Text>
                </Legend>
              )}
              <CardsWrapper hasPaginator={pager?.number_of_pages > 1}>
                {courses.map((course, i) => (
                  <React.Fragment key={`card-${i}`}>
                    <CourseCard>
                      <Text type="b5">{course.code}</Text>
                      <Column className="ellipsis" rowGap={4}>
                        {course?.typology && (
                          <Text type="b4" className="typology">
                            {course?.typology === 'event' ? 'Evento' : 'Corso'}
                          </Text>
                        )}
                        <Text
                          type="h3"
                          className="ellipsis"
                          onClick={() =>
                            navigate(
                              `${routes.courseStatistics.path}/${course?.id}/${
                                course?.typology === 'event'
                                  ? 'sessioni'
                                  : 'attività'
                              }`
                            )
                          }
                        >
                          {course.title}
                        </Text>
                      </Column>
                      <Text type="b4" className="ellipsis">
                        {course?.category || '-'}
                      </Text>
                      <ProgressBar
                        percentage={Math.round(course?.completion_rate || 0)}
                        hasPercentage
                        color="#22408B"
                        bgColor="#D1D1D1"
                        height={8}
                        rearrange
                        mb={-4}
                      />
                      <Text type="b4">
                        {timestampToDate(course?.course_start_date)}
                      </Text>
                      <Text type="b4">
                        {timestampToDate(course?.course_end_date)}
                      </Text>
                      <Text type="b4">
                        {course?.published ? 'Visibile' : 'Non visibile'}
                      </Text>
                      <Text type="b4">
                        {timestampToDate(course?.publication_date)}
                      </Text>
                      <CTA
                        type="tertiary"
                        onClick={() => {
                          onClickCard(course);
                        }}
                        btnText={
                          course?.id === activeCardId ? 'Chiudi' : 'Esplora'
                        }
                        icon={course?.id === activeCardId ? 'minus' : 'plus'}
                      />
                    </CourseCard>
                    {course?.id === activeCardId ? (
                      course?.typology === 'event' ? (
                        <ActivitiesStats activities={activities} isEvent />
                      ) : (
                        <ActivitiesStats activities={activities?.sections} />
                      )
                    ) : null}
                  </React.Fragment>
                ))}
              </CardsWrapper>
              <Pagination
                pager={pager}
                callBack={loadMore}
                setItemsPerPage={setItemsPerPage}
              />
            </>
          ) : (
            <NoResult />
          )}
        </section>
      </Container>
      <ModalUserGroups />
      <ModalSupervisorUserLog />
    </div>
  );
};

TutorStatistics.propTypes = {
  mediaIsPhone: PropTypes.bool,
  mediaIsTablet: PropTypes.bool,
  // state
  user: PropTypes.object,
  courses: PropTypes.array,
  activities: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  filters: PropTypes.object,
  pager: PropTypes.object,
  categories: PropTypes.array,
  groupsPager: PropTypes.object,
  groupsList: PropTypes.array,

  // dispatch
  modalOpen: PropTypes.func,
  getCategories: PropTypes.func,
  getUserStats: PropTypes.func,
  getUserCourses: PropTypes.func,
  getCourseUsersActivities: PropTypes.func,
  courseUserActivitiesReset: PropTypes.func,
  getEventUsersActivities: PropTypes.func,
};

export default connect(
  (state) => {
    const {
      user,
      courses: { list: courses, activities, filters, pager },
      groups: { pager: groupsPager, list: groupsList },
    } = state.statistics;
    const { categories } = state.courselist;
    return {
      user,
      courses,
      activities,
      filters,
      pager,
      categories,
      groupsPager,
      groupsList,
    };
  },
  (dispatch) => ({
    modalOpen: (id) => dispatch({ type: MODAL_OPEN, id }),
    getCategories: (filters) =>
      dispatch({ type: COURSE_CATEGORIES_GET._REQUEST, filters }),
    getUserStats: ({ user_id }) =>
      dispatch({ type: USER_STATS_GET._REQUEST, user_id }),
    getUserCourses: (filters) =>
      dispatch({ type: USER_COURSES_GET._REQUEST, filters }),
    getCourseUsersActivities: (course_id, user_id) =>
      dispatch({
        type: COURSE_USERS_ACTIVITIES_GET._REQUEST,
        course_id,
        user_id,
      }),
    getEventUsersActivities: (course_id, user_id) =>
      dispatch({
        type: EVENT_USERS_ACTIVITIES_GET._REQUEST,
        course_id,
        user_id,
      }),
    courseUserActivitiesReset: () =>
      dispatch({ type: COURSE_USERS_ACTIVITIES_RESET }),
  })
)(withMediaQueries(TutorStatistics));
